import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';

const TOKENS = [
    {
        symbol: 'ETH',
        name: 'Ethereum',
        icon: '⟠',
        coingeckoId: 'ethereum',
        fallbackPrice: 3000,
    },
    {
        symbol: 'USDT',
        name: 'Tether USD',
        icon: '₮',
        coingeckoId: 'tether',
        fallbackPrice: 1.0,
    },
    {
        symbol: 'USDC',
        name: 'USD Coin',
        icon: '$',
        coingeckoId: 'usd-coin',
        fallbackPrice: 1.0,
    },
];

async function fetchPrices() {
    const ids = TOKENS.map(t => t.coingeckoId).join(',');
    try {
        const res = await fetch(
            `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd`,
            { signal: AbortSignal.timeout(6000) }
        );
        if (!res.ok) throw new Error('CoinGecko error');
        const json = await res.json();
        const prices = {};
        TOKENS.forEach(t => {
            prices[t.symbol] = json?.[t.coingeckoId]?.usd ?? t.fallbackPrice;
        });
        return prices;
    } catch {
        const fallback = {};
        TOKENS.forEach(t => { fallback[t.symbol] = t.fallbackPrice; });
        return fallback;
    }
}

function DepositModal({ walletAddress, onClose }) {
    const [selected, setSelected] = useState('ETH');
    const [copied, setCopied]     = useState(false);
    const [prices, setPrices]     = useState(null);

    useEffect(() => {
        fetchPrices().then(setPrices);
    }, []);

    const copyAddress = () => {
        if (!walletAddress) return;
        navigator.clipboard.writeText(walletAddress);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    const token     = TOKENS.find(t => t.symbol === selected);
    const price     = prices?.[selected];
    const shortAddr = walletAddress
        ? `${walletAddress.slice(0, 10)}…${walletAddress.slice(-8)}`
        : '—';

    return (
        <>
            <div style={s.overlay} onClick={onClose} aria-hidden="true" />
            <div style={s.sheet} role="dialog" aria-label="Deposit crypto">
                <div style={s.handle} aria-hidden="true" />

                {/* Header */}
                <div style={s.header}>
                    <div style={s.headerIcon}>
                        <i className="bx bx-download" style={{ color: '#19bfb1', fontSize: 22 }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={s.title}>Deposit Funds</div>
                        <div style={s.subtitle}>Select token and copy your address</div>
                    </div>
                    <button onClick={onClose} style={s.closeBtn} aria-label="Close">
                        <i className="bx bx-x" style={{ fontSize: 20 }} />
                    </button>
                </div>

                {/* Token selector */}
                <div style={s.tokenRow}>
                    {TOKENS.map(t => (
                        <button
                            key={t.symbol}
                            onClick={() => setSelected(t.symbol)}
                            style={{ ...s.tokenBtn, ...(selected === t.symbol ? s.tokenBtnActive : {}) }}
                        >
                            <span style={s.tokenIcon}>{t.icon}</span>
                            <div>
                                <div style={s.tokenSymbol}>{t.symbol}</div>
                                <div style={s.tokenName}>{t.name}</div>
                            </div>
                            {selected === t.symbol && (
                                <i className="bx bx-check-circle" style={s.tokenCheck} />
                            )}
                        </button>
                    ))}
                </div>

                {/* QR code */}
                <div style={s.qrWrap}>
                    {walletAddress ? (
                        <QRCode
                            value={walletAddress}
                            size={160}
                            bgColor="#0f0a4a"
                            fgColor="#ffffff"
                            level="M"
                        />
                    ) : (
                        <div style={s.qrPlaceholder}>No address</div>
                    )}
                </div>

                {/* Address display */}
                <div style={s.addrLabel}>Your Ethereum Wallet Address</div>
                <div style={s.addrBox}>{shortAddr}</div>
                <button onClick={copyAddress} style={s.copyBtn}>
                    <i className={`bx ${copied ? 'bx-check' : 'bx-copy'}`} />
                    {copied ? ' Copied!' : ' Copy Address'}
                </button>

                {/* Price + warning */}
                {price && (
                    <div style={s.rateRow}>
                        <i className="bx bx-line-chart" style={{ color: '#19bfb1', marginRight: 6 }} />
                        1 {selected} = ${price.toLocaleString('en-US', { minimumFractionDigits: selected === 'ETH' ? 0 : 2, maximumFractionDigits: selected === 'ETH' ? 0 : 4 })} USD
                    </div>
                )}

                <div style={s.warning}>
                    <i className="bx bx-error-circle" style={{ color: '#f59e0b', marginRight: 6, flexShrink: 0 }} />
                    <span>
                        Only send <strong>{token?.name}</strong> ({selected}) on the{' '}
                        <strong>Ethereum network</strong> to this address.
                        Deposits confirm after 12 blocks (~3 min). All deposits are credited as USD.
                    </span>
                </div>
            </div>

        </>
    );
}

const s = {
    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        zIndex: 200,
    },
    sheet: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(180deg, #0f0a4a 0%, #0c0836 100%)',
        borderRadius: '20px 20px 0 0',
        padding: '0 20px 40px',
        zIndex: 201,
        animation: 'sheetUp 0.3s ease',
        maxHeight: '92vh',
        overflowY: 'auto',
    },
    handle: {
        width: 36,
        height: 4,
        background: 'rgba(255,255,255,0.2)',
        borderRadius: 2,
        margin: '12px auto 0',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '20px 0 16px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        marginBottom: 20,
    },
    headerIcon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        background: 'rgba(25,191,177,0.15)',
        border: '1px solid rgba(25,191,177,0.25)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    title: {
        color: '#fff',
        fontSize: 16,
        fontWeight: 700,
    },
    subtitle: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 12,
        marginTop: 2,
    },
    closeBtn: {
        background: 'rgba(255,255,255,0.08)',
        border: 'none',
        borderRadius: 8,
        color: 'rgba(255,255,255,0.7)',
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
    },
    tokenRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginBottom: 24,
    },
    tokenBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 14,
        cursor: 'pointer',
        color: '#fff',
        textAlign: 'left',
        transition: 'all 0.2s',
    },
    tokenBtnActive: {
        background: 'rgba(25,191,177,0.12)',
        border: '1px solid rgba(25,191,177,0.4)',
    },
    tokenIcon: {
        fontSize: 20,
        width: 36,
        height: 36,
        background: 'rgba(255,255,255,0.08)',
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        lineHeight: 1,
        textAlign: 'center',
        paddingTop: 6,
    },
    tokenSymbol: {
        fontSize: 14,
        fontWeight: 700,
        color: '#fff',
    },
    tokenName: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.5)',
        marginTop: 2,
    },
    tokenCheck: {
        marginLeft: 'auto',
        color: '#19bfb1',
        fontSize: 18,
    },
    qrWrap: {
        display: 'flex',
        justifyContent: 'center',
        padding: '8px 0 20px',
        background: 'rgba(255,255,255,0.04)',
        borderRadius: 16,
        marginBottom: 16,
    },
    qrPlaceholder: {
        width: 160,
        height: 160,
        background: 'rgba(255,255,255,0.05)',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'rgba(255,255,255,0.3)',
        fontSize: 13,
    },
    addrLabel: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        marginBottom: 8,
    },
    addrBox: {
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 10,
        padding: '12px 14px',
        color: '#fff',
        fontSize: 13,
        fontFamily: 'monospace',
        wordBreak: 'break-all',
        marginBottom: 10,
    },
    copyBtn: {
        width: '100%',
        padding: '13px',
        background: 'rgba(25,191,177,0.15)',
        border: '1px solid rgba(25,191,177,0.35)',
        borderRadius: 12,
        color: '#19bfb1',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        marginBottom: 14,
    },
    rateRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'rgba(255,255,255,0.6)',
        fontSize: 13,
        marginBottom: 14,
    },
    warning: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0,
        background: 'rgba(245,158,11,0.08)',
        border: '1px solid rgba(245,158,11,0.2)',
        borderRadius: 12,
        padding: '12px 14px',
        color: 'rgba(255,255,255,0.65)',
        fontSize: 12,
        lineHeight: 1.6,
    },
};

export default DepositModal;
